import { useState } from "react";
import {
  CodeEditor,
  Badge,
  Button,
  type CodeEditorFrame,
  type CodeEditorMode,
} from "@one-nexty/components-web";
import { AnimatedCopyIcon } from "@one-nexty/icons";
import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";

const SAMPLE_TSX = `import { useState } from "react";
import { CodeEditor, Button } from "@one-nexty/components-web";

export function CloudDeploymentCard({ name, status }: { name: string; status: string }) {
  const [active, setActive] = useState(true);

  return (
    <div className="p-6 rounded-2xl bg-surface-raised border border-border">
      <h3 className="text-lg font-semibold text-fg">{name}</h3>
      <p className="text-sm text-fg-muted">Statut: {status}</p>
      <Button onClick={() => setActive(!active)} variant={active ? "primary" : "outline"}>
        {active ? "Actif" : "En pause"}
      </Button>
    </div>
  );
}`;

const SAMPLE_GO = `package main

import (
	"fmt"
	"net/http"
	"time"
)

type Server struct {
	Port int
	Name string
}

func (s *Server) Start() error {
	http.HandleFunc("/health", func(w http.ResponseWriter, r *http.Request) {
		w.WriteHeader(http.StatusOK)
		fmt.Fprintf(w, ` + "`" + `{"status":"ok","timestamp":"%s"}` + "`" + `, time.Now().Format(time.RFC3339))
	})
	return http.ListenAndServe(fmt.Sprintf(":%d", s.Port), nil)
}`;

const SAMPLE_PYTHON = `from fastapi import FastAPI, Depends, HTTPException
from pydantic import BaseModel

app = FastAPI(title="Nexty Cloud IAM API", version="1.0.0")

class UserToken(BaseModel):
    user_id: str
    roles: list[str]
    is_active: bool = True

@app.post("/v1/auth/verify")
async def verify_token(token: str):
    if not token.startswith("nxt_"):
        raise HTTPException(status_code=401, detail="Jeton de sécurité invalide")
    return {"status": "authenticated", "claims": {"user_id": "usr_94a2b1", "role": "admin"}}`;

const SAMPLE_ORIGINAL_GO = `func ProcessQueue(items []string) {
	for _, item := range items {
		println(item)
	}
}`;

const SAMPLE_MODIFIED_GO = `func ProcessQueue(ctx context.Context, items []string) error {
	g, ctx := errgroup.WithContext(ctx)
	for _, item := range items {
		item := item
		g.Go(func() error {
			return processItem(ctx, item)
		})
	}
	return g.Wait()
}`;

export function EditorsSection() {
  const [interactiveLang, setInteractiveLang] = useState<string>("typescript");
  const [selectedFrame, setSelectedFrame] = useState<CodeEditorFrame>("macos");
  const [selectedMode, setSelectedMode] = useState<CodeEditorMode>("monaco");
  const [editorCode, setEditorCode] = useState<string>(SAMPLE_TSX);
  const [demoCopied, setDemoCopied] = useState(false);

  const handleLangChange = (lang: string) => {
    setInteractiveLang(lang);
    if (lang === "typescript") setEditorCode(SAMPLE_TSX);
    else if (lang === "go") setEditorCode(SAMPLE_GO);
    else if (lang === "python") setEditorCode(SAMPLE_PYTHON);
  };

  return (
    <Section
      id="editeurs"
      title="Éditeurs & Code Highlighting"
      description="Composants d'édition et d'affichage de code SOTA. Monaco interactif, coloration syntaxique native Prism adaptée aux tokens OKLCH de notre thème, fenêtres style macOS, Sobre et Glass."
    >
      {/* ------------------------------------------------------------------ 1. Studio d'Éditeur Monaco SOTA */}
      <SubSection
        title="Studio d'Éditeur Interactif SOTA"
        hint="Sélectionnez le mode (Monaco, Rapide, Aperçu) et la variante de fenêtre (macOS, Sobre, Glass)."
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 bg-surface-raised/60 p-3 rounded-xl border border-border">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-fg-muted uppercase tracking-wider">Langage:</span>
            <Button
              size="xs"
              variant={interactiveLang === "typescript" ? "primary" : "outline"}
              onClick={() => handleLangChange("typescript")}
            >
              TypeScript / TSX
            </Button>
            <Button
              size="xs"
              variant={interactiveLang === "go" ? "primary" : "outline"}
              onClick={() => handleLangChange("go")}
            >
              Go
            </Button>
            <Button
              size="xs"
              variant={interactiveLang === "python" ? "primary" : "outline"}
              onClick={() => handleLangChange("python")}
            >
              Python
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-fg-muted uppercase tracking-wider">Mode & Fenêtre:</span>
            <Button
              size="xs"
              variant={selectedMode === "monaco" ? "primary" : "outline"}
              onClick={() => setSelectedMode("monaco")}
            >
              Monaco
            </Button>
            <Button
              size="xs"
              variant={selectedMode === "interactive" ? "primary" : "outline"}
              onClick={() => setSelectedMode("interactive")}
            >
              Rapide
            </Button>
            <Button
              size="xs"
              variant={selectedMode === "viewer" ? "primary" : "outline"}
              onClick={() => setSelectedMode("viewer")}
            >
              Aperçu
            </Button>
            <span className="h-4 w-px bg-border mx-1" />
            <Button
              size="xs"
              variant={selectedFrame === "macos" ? "secondary" : "ghost"}
              onClick={() => setSelectedFrame("macos")}
            >
              macOS
            </Button>
            <Button
              size="xs"
              variant={selectedFrame === "sobre" ? "secondary" : "ghost"}
              onClick={() => setSelectedFrame("sobre")}
            >
              Sobre
            </Button>
            <Button
              size="xs"
              variant={selectedFrame === "glass" ? "secondary" : "ghost"}
              onClick={() => setSelectedFrame("glass")}
            >
              Glass
            </Button>
          </div>
        </div>

        <Preview layout="stack">
          <CodeEditor
            value={editorCode}
            onChange={setEditorCode}
            language={interactiveLang}
            frame={selectedFrame}
            mode={selectedMode}
            showStatusBar
            showCopyButton
            height={380}
            description="Éditeur interactif alimenté par le Design System Nexty"
          />
        </Preview>
      </SubSection>

      {/* ------------------------------------------------------------------ 2. Styles d'Habillage (macOS vs Sobre vs Glass) */}
      <SubSection
        title="Variantes d'Habillage & Fenêtrage"
        hint="Style macOS avec feux tricolores, sobre pour tableau de bord, ou glassmorphic."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2 flex items-center gap-2">
              <Badge tone="brand">macOS Windowed</Badge>
              Fenêtré avec Feux Tricolores
            </h4>
            <CodeEditor
              value={`const config = {\n  env: "production",\n  region: "eu-west-3",\n  replicas: 5\n};`}
              language="typescript"
              frame="macos"
              mode="viewer"
              title="config.production.ts"
              height={200}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2 flex items-center gap-2">
              <Badge tone="neutral">Glassmorphic</Badge>
              Translucide & Sobriété moderne
            </h4>
            <CodeEditor
              value={`SELECT id, name, status, created_at\nFROM cloud_instances\nWHERE status = 'running'\nORDER BY created_at DESC;`}
              language="sql"
              frame="glass"
              mode="viewer"
              title="instances_query.sql"
              height={200}
            />
          </div>
        </div>
      </SubSection>

      {/* ------------------------------------------------------------------ 3. Comparateur de Code (Diff Engine) */}
      <SubSection
        title="Comparateur de Code (Mode Diff)"
        hint="Visualisez immédiatement les modifications apportées à un fichier source côte à côte."
      >
        <Preview>
          <CodeEditor
            value={SAMPLE_MODIFIED_GO}
            originalValue={SAMPLE_ORIGINAL_GO}
            language="go"
            frame="macos"
            mode="diff"
            title="queue_processor.go (Diff)"
            height={260}
          />
        </Preview>
      </SubSection>

      {/* ------------------------------------------------------------------ 4. Highlighting & Surbrillance de Lignes */}
      <SubSection
        title="Coloration Syntaxique & Surbrillance de Lignes Target"
        hint="Chaque token est stylisé selon notre palette OKLCH. Surbrillance de lignes cibles."
      >
        <Preview>
          <CodeEditor
            value={SAMPLE_PYTHON}
            language="python"
            frame="sobre"
            mode="viewer"
            title="auth_service.py"
            highlightLines={[11, 12, 13]}
            height={280}
            description="Lignes 11-13 en surbrillance d'accentuation"
          />
        </Preview>
      </SubSection>

      {/* ------------------------------------------------------------------ 5. Copie Animée */}
      <SubSection
        title="Action Copier avec AnimatedCopyIcon"
        hint="Micro-animation ressort et particules de validation lors de la copie."
      >
        <div className="flex flex-wrap items-center gap-4 bg-surface-raised p-6 rounded-2xl border border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setDemoCopied(true);
              setTimeout(() => setDemoCopied(false), 2000);
            }}
            className="gap-2 font-mono text-xs"
          >
            <AnimatedCopyIcon copied={demoCopied} size={16} />
            <span>{demoCopied ? "Copié !" : "Tester l'animation de copie"}</span>
          </Button>
        </div>
      </SubSection>
    </Section>
  );
}
